import React from "react";
import {
  MessageCircle,
  Calendar,
  Users,
  BarChart3,
  Dumbbell,
  Apple,
  Check,
} from "lucide-react";
import { AnimatedSection } from "./AnimatedSection";

interface TrainerFeaturesProps {
  visibleSections: Record<string, boolean>;
}

export const TrainerFeatures: React.FC<TrainerFeaturesProps> = ({
  visibleSections,
}) => {
  const features = [
    {
      icon: MessageCircle,
      title: "Comunicação",
      desc: "Chat com aluno e emails automáticos para manter o engajamento.",
      features: ["Chat integrado", "Emails automáticos"],
      color: "purple",
      iconClass: "text-purple-600 dark:text-purple-400",
      checkClass: "text-purple-600 dark:text-purple-400",
    },
    {
      icon: Calendar,
      title: "Agenda",
      desc: "Gerencie sua disponibilidade e aulas com facilidade.",
      features: [
        "Gestão de disponibilidade",
        "Confirmação de aulas",
        "Export para Excel",
      ],
      color: "blue",
      iconClass: "text-blue-600 dark:text-blue-400",
      checkClass: "text-blue-600 dark:text-blue-400",
    },
    {
      icon: Users,
      title: "Gestão de alunos",
      desc: "Controle completo dos seus alunos em um só lugar.",
      features: ["Aniversariantes", "Notificações", "Controle de validade"],
      color: "green",
      iconClass: "text-green-600 dark:text-green-400",
      checkClass: "text-green-600 dark:text-green-400",
    },
    {
      icon: BarChart3,
      title: "Avaliação 360",
      desc: "Crie avaliações completas com ajuda da IA.",
      features: [
        "Criação por IA (foto e áudio)",
        "Histórico completo",
        "Análise de sono e estresse",
      ],
      color: "pink",
      iconClass: "text-pink-600 dark:text-pink-400",
      checkClass: "text-pink-600 dark:text-pink-400",
    },
    {
      icon: Dumbbell,
      title: "Treinos",
      desc: "IA para criar treinos personalizados rapidamente.",
      features: [
        "Auxilio na criação de treinos por IA",
        "Dashboard de desempenho",
        "Histórico de treinos",
      ],
      color: "orange",
      iconClass: "text-orange-600 dark:text-orange-400",
      checkClass: "text-orange-600 dark:text-orange-400",
    },
    {
      icon: Apple,
      title: "Nutrição",
      desc: "Acompanhe a alimentação dos seus alunos.",
      features: ["Acompanhamento detalhado", "Análise de nutrientes"],
      color: "red",
      iconClass: "text-red-600 dark:text-red-400",
      checkClass: "text-red-600 dark:text-red-400",
    },
  ];

  return (
    <section className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-purple-50 dark:from-black dark:to-purple-950/30 transition-colors duration-300">
      <div className="container mx-auto max-w-7xl">
        <AnimatedSection
          id="trainers-header"
          className="text-center mb-12 sm:mb-20"
          visible={visibleSections["trainers-header"] ?? true}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 tracking-tight text-gray-900 dark:text-white">
            Para personal trainers
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 font-light">
            Ferramentas profissionais para gerenciar seus alunos
          </p>
        </AnimatedSection>

        {/* Grid de funcionalidades */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-12 sm:mb-20">
          {features.map((feature, i) => (
            <AnimatedSection
              key={i}
              id={`trainer-feature-${i}`}
              delay={i * 100}
              className="group bg-white/80 dark:bg-white/5 backdrop-blur-sm p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-white/10 hover:bg-white dark:hover:bg-white/10 hover:border-purple-300 dark:hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
              visible={visibleSections[`trainer-feature-${i}`] ?? true}
            >
              <feature.icon
                className={`w-10 sm:w-12 h-10 sm:h-12 mb-4 sm:mb-6 ${feature.iconClass} group-hover:scale-110 transition-transform duration-300`}
              />
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900 dark:text-white">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
                {feature.desc}
              </p>
              <ul className="space-y-2 sm:space-y-3 text-gray-700 dark:text-gray-300">
                {feature.features.map((item, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <Check
                      className={`w-4 sm:w-5 h-4 sm:h-5 ${feature.checkClass} mt-0.5 flex-shrink-0`}
                    />
                    <span className="text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          ))}
        </div>

        {/* Feature Image */}
        <AnimatedSection
          id="trainer-dashboard"
          className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl group"
          visible={visibleSections["trainer-dashboard"] ?? true}
        >
          <img
            src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1600&h=800&fit=crop"
            alt="Personal Trainer Dashboard"
            className="w-full h-auto transform group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white/95 dark:from-purple-950/95 to-transparent group-hover:from-white dark:group-hover:from-purple-950 transition-all duration-500"></div>
          <div className="absolute bottom-0 left-0 p-6 sm:p-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4 text-gray-900 dark:text-white">
              Dashboard completo
            </h3>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-2xl">
              Acompanhe indicadores de alunos, treinos e engajamento em tempo
              real.
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};
